import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRightLeft,
  ChevronDown,
  Download,
  FilePlus2,
  FolderTree,
  History,
  Image as ImageIcon,
  KeyRound,
  LogIn,
  MapPin,
  Pencil,
  RefreshCw,
  Search,
  Settings2,
  ShieldAlert,
  Trash2,
  Upload,
  UserCog,
  X,
} from "lucide-react";
import { API, Button, Card, EmptyState, PageHeader, Skeleton, apiFetch, cx } from "./_shared";

const ENTITY_META = {
  location: { label: "Địa điểm", icon: MapPin, tone: "bg-emerald-100 text-emerald-700" },
  category: { label: "Danh mục", icon: FolderTree, tone: "bg-amber-100 text-amber-700" },
  media: { label: "Media", icon: ImageIcon, tone: "bg-violet-100 text-violet-700" },
  user: { label: "Người dùng", icon: UserCog, tone: "bg-sky-100 text-sky-700" },
  setting: { label: "Cài đặt", icon: Settings2, tone: "bg-slate-200 text-slate-700" },
  auth: { label: "Đăng nhập", icon: LogIn, tone: "bg-rose-100 text-rose-700" },
};

const ACTION_META = {
  create: { label: "Tạo mới", icon: FilePlus2 },
  update: { label: "Chỉnh sửa", icon: Pencil },
  delete: { label: "Xoá", icon: Trash2 },
  status: { label: "Đổi trạng thái", icon: ArrowRightLeft },
  move: { label: "Di chuyển", icon: ArrowRightLeft },
  rename: { label: "Đổi tên", icon: Pencil },
  upload: { label: "Tải lên", icon: Upload },
  restore: { label: "Khôi phục", icon: History },
  reset_password: { label: "Đặt lại mật khẩu", icon: KeyRound },
  role_change: { label: "Đổi vai trò", icon: UserCog },
  login: { label: "Đăng nhập", icon: LogIn },
  login_failed: { label: "Đăng nhập thất bại", icon: ShieldAlert },
  bulk_status: { label: "Đổi trạng thái hàng loạt", icon: ArrowRightLeft },
  bulk_delete: { label: "Xoá hàng loạt", icon: Trash2 },
  bulk_category: { label: "Chuyển danh mục hàng loạt", icon: FolderTree },
};

const FIELD_LABEL = {
  name: "Tên", slug: "Slug", category: "Danh mục", status: "Trạng thái", ward: "Phường/xã", district: "Quận/huyện", city: "Thành phố",
  phone: "Điện thoại", shortIntro: "Giới thiệu ngắn", description: "Mô tả", image: "Ảnh đại diện", gallery: "Thư viện ảnh",
  heroGallery: "Ảnh banner", videos: "Video", mediaBlocks: "Khối nội dung", price: "Giá", priceMin: "Giá thấp nhất", priceMax: "Giá cao nhất",
  openTime: "Giờ mở", closeTime: "Giờ đóng", latitude: "Vĩ độ", longitude: "Kinh độ", mapIcon: "Icon bản đồ", seoTitle: "Tiêu đề SEO",
  seoDescription: "Mô tả SEO", seoKeywords: "Từ khoá SEO", highlights: "Điểm nổi bật", tips: "Mẹo", faq: "Câu hỏi thường gặp",
  tiktokUrl: "TikTok", homeLinkUrl: "Liên kết trang chủ", homeLinkLabel: "Nhãn liên kết", isActive: "Hiển thị", icon: "Icon", iconImage: "Icon (ảnh)",
  fullName: "Họ tên", email: "Email", username: "Tên đăng nhập", role: "Vai trò", adminRole: "Quyền quản trị", emailVerified: "Đã xác minh email", authProvider: "Đăng nhập bằng",
};

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const relTime = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "Vừa xong";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} phút trước`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} giờ trước`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d} ngày trước` : new Date(iso).toLocaleDateString("vi-VN");
};

const show = (v) => (v == null || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

function Row({ item, open, onToggle }) {
  const ent = ENTITY_META[item.entity] || { label: item.entity, icon: History, tone: "bg-admin-muted text-admin-textMuted" };
  const act = ACTION_META[item.action] || { label: item.action, icon: History };
  const Icon = ent.icon;
  const danger = item.action === "login_failed" || item.action === "delete" || item.action === "bulk_delete";
  const hasDetail = (item.changes?.length || 0) > 0 || Object.keys(item.meta || {}).length > 0;

  return (
    <li className="border-b border-admin-border last:border-0">
      <button
        type="button"
        onClick={hasDetail ? onToggle : undefined}
        aria-expanded={hasDetail ? open : undefined}
        className={cx("flex w-full items-start gap-3 px-4 py-3.5 text-left transition", hasDetail ? "hover:bg-admin-muted/50" : "cursor-default")}
      >
        <span className={cx("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", ent.tone)}>
          <Icon size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cx("rounded-md px-1.5 py-0.5 text-[11px] font-semibold", danger ? "bg-rose-50 text-rose-700" : "bg-admin-muted text-admin-textMuted")}>{act.label}</span>
            <span className="rounded-md bg-admin-muted px-1.5 py-0.5 text-[11px] font-medium text-admin-textMuted">{ent.label}</span>
            {item.entityName ? <span className="truncate text-sm font-semibold text-admin-text">{item.entityName}</span> : null}
          </span>
          <span className="mt-1 block text-sm text-admin-text">{item.summary}</span>
          <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-admin-textMuted">
            <span>
              <span className="font-semibold text-admin-text">{item.actorName || "Không xác định"}</span>
              {item.actorRole ? ` · ${item.actorRole}` : ""}
            </span>
            <time dateTime={item.createdAt} title={fmtTime(item.createdAt)}>{relTime(item.createdAt)}</time>
            {item.ip ? <span>IP {item.ip}</span> : null}
          </span>
        </span>
        {hasDetail ? <ChevronDown size={16} className={cx("mt-1 shrink-0 text-admin-textMuted transition", open && "rotate-180")} /> : null}
      </button>

      {open && hasDetail ? (
        <div className="space-y-3 bg-admin-muted/40 px-4 pb-4 pt-1 sm:pl-16">
          {item.changes?.length ? (
            <div className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
              <table className="w-full text-xs">
                <thead className="bg-admin-muted/70 text-[10px] uppercase tracking-wider text-admin-textMuted">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Trường</th>
                    <th className="px-3 py-2 text-left font-semibold">Trước</th>
                    <th className="px-3 py-2 text-left font-semibold">Sau</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-border">
                  {item.changes.map((c, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold text-admin-text">{FIELD_LABEL[c.field] || c.field}</td>
                      <td className="max-w-[260px] break-words px-3 py-2 text-rose-700/90">{show(c.from)}</td>
                      <td className="max-w-[260px] break-words px-3 py-2 text-emerald-700">{show(c.to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {Object.keys(item.meta || {}).length ? (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
              {Object.entries(item.meta)
                .filter(([, v]) => v !== undefined && v !== null && v !== "")
                .map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="shrink-0 text-admin-textMuted">{k}:</dt>
                    <dd className="min-w-0 break-words text-admin-text">{show(v)}</dd>
                  </div>
                ))}
            </dl>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

const inputCls =
  "h-9 rounded-lg border border-admin-border bg-admin-surface px-2.5 text-sm text-admin-text focus:border-admin-primary focus:outline-none focus:ring-4 focus:ring-admin-primary/10";

export default function AdminAudit() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    entity: params.get("entity") || "",
    action: params.get("action") || "",
    actor: params.get("actor") || "",
    q: "",
    from: "",
    to: "",
  }));
  const [facets, setFacets] = useState({ entities: [], actors: [], actions: [] });
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [moreError, setMoreError] = useState("");
  const [openId, setOpenId] = useState(null);
  const reqId = useRef(0);

  const buildQuery = useCallback(
    (after) => {
      const p = new URLSearchParams({ limit: "30" });
      Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
      if (after) p.set("cursor", after);
      return p.toString();
    },
    [filters]
  );

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError("");
    setMoreError("");
    try {
      const res = await apiFetch(`${API}/audit?${buildQuery()}`);
      if (id !== reqId.current) return;
      setItems(res.items || []);
      setCursor(res.nextCursor || null);
    } catch (e) {
      if (id === reqId.current) setError(e?.status === 403 ? "Vai trò của bạn không có quyền xem nhật ký thao tác." : e?.message || "Không tải được nhật ký");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    const t = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, filters.q]);

  useEffect(() => {
    apiFetch(`${API}/audit/facets`).then(setFacets).catch(() => {});
  }, []);

  // Đồng bộ bộ lọc chính lên URL để chia sẻ/quay lại được
  useEffect(() => {
    const next = {};
    ["entity", "action", "actor"].forEach((k) => filters[k] && (next[k] = filters[k]));
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.entity, filters.action, filters.actor]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    const id = reqId.current; // nếu bộ lọc đổi trong lúc chờ, bỏ kết quả cũ
    setLoadingMore(true);
    setMoreError("");
    try {
      const res = await apiFetch(`${API}/audit?${buildQuery(cursor)}`);
      if (id !== reqId.current) return;
      setItems((prev) => [...prev, ...(res.items || [])]);
      setCursor(res.nextCursor || null);
    } catch (e) {
      if (id === reqId.current) setMoreError(e?.message || "Không tải thêm được");
    } finally {
      setLoadingMore(false);
    }
  };

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const hasFilter = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  const exportCsv = () => {
    const head = ["Thời gian", "Người thực hiện", "Vai trò", "Hành động", "Đối tượng", "Tên", "Mô tả", "IP"];
    const rows = items.map((i) => [
      fmtTime(i.createdAt), i.actorName, i.actorRole, (ACTION_META[i.action] || {}).label || i.action,
      (ENTITY_META[i.entity] || {}).label || i.entity, i.entityName, i.summary, i.ip,
    ]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const blob = new Blob(["﻿" + [head, ...rows].map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nhat-ky-thao-tac_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full pb-6">
      <PageHeader
        title="Nhật ký thao tác"
        subtitle="Ai đã sửa gì, lúc nào — lưu 13 tháng, không thể chỉnh sửa."
        action={
          <>
            <Button variant="secondary" icon={Download} onClick={exportCsv} disabled={!items.length}>
              Xuất CSV (dòng đã tải)
            </Button>
            <Button variant="secondary" icon={RefreshCw} onClick={load} loading={loading}>
              Làm mới
            </Button>
          </>
        }
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <div className="relative sm:col-span-2">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-admin-textMuted" />
            <input value={filters.q} onChange={set("q")} placeholder="Tìm theo mô tả, tên, người thực hiện…" aria-label="Tìm trong nhật ký" className={cx(inputCls, "w-full pl-8")} />
          </div>
          <select value={filters.entity} onChange={set("entity")} aria-label="Đối tượng" className={inputCls}>
            <option value="">Mọi đối tượng</option>
            {facets.entities.map((e) => (
              <option key={e} value={e}>{ENTITY_META[e]?.label || e}</option>
            ))}
          </select>
          <select value={filters.action} onChange={set("action")} aria-label="Hành động" className={inputCls}>
            <option value="">Mọi hành động</option>
            {facets.actions.map((a) => (
              <option key={a.action} value={a.action}>{ACTION_META[a.action]?.label || a.action} ({a.count})</option>
            ))}
          </select>
          <select value={filters.actor} onChange={set("actor")} aria-label="Người thực hiện" className={inputCls}>
            <option value="">Mọi người</option>
            {facets.actors.map((a) => (
              <option key={a.id} value={a.id}>{a.name} ({a.count})</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <input type="date" value={filters.from} onChange={set("from")} aria-label="Từ ngày" className={cx(inputCls, "min-w-0 flex-1")} />
            <span className="text-admin-textMuted">–</span>
            <input type="date" value={filters.to} onChange={set("to")} aria-label="Đến ngày" className={cx(inputCls, "min-w-0 flex-1")} />
          </div>
        </div>
        {hasFilter ? (
          <div className="flex items-center justify-between border-t border-admin-border px-4 py-2 text-xs text-admin-textMuted">
            <span>Đang lọc kết quả</span>
            <button type="button" onClick={() => setFilters({ entity: "", action: "", actor: "", q: "", from: "", to: "" })} className="inline-flex items-center gap-1 font-semibold text-admin-primary hover:underline">
              <X size={12} /> Xoá bộ lọc
            </button>
          </div>
        ) : null}
      </Card>

      <Card className="overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="space-y-4 p-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-9 w-9 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState icon={AlertTriangle} title="Không xem được nhật ký" description={error} />
        ) : items.length === 0 ? (
          <EmptyState icon={History} title="Chưa có thao tác nào" description={hasFilter ? "Không có kết quả khớp bộ lọc." : "Mọi thao tác của quản trị viên sẽ được ghi lại ở đây."} />
        ) : (
          <>
            <ul>
              {items.map((it) => (
                <Row key={it._id} item={it} open={openId === it._id} onToggle={() => setOpenId((o) => (o === it._id ? null : it._id))} />
              ))}
            </ul>
            {cursor ? (
              <div className="border-t border-admin-border p-3">
                {moreError ? <p className="mb-2 text-center text-xs text-admin-danger">{moreError}</p> : null}
                <Button variant="secondary" className="w-full" onClick={loadMore} loading={loadingMore}>
                  {moreError ? "Thử lại" : "Tải thêm"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
